import { useQuery } from "@tanstack/react-query";
import { createClient } from "@wtc/ui";
import { useUniversalAuth } from "@wtc/ui";
import { buildAllUsersQuery } from "@wtc/supabase";
import type { User } from "@/lib/types/user";

export function useCurrentUser() {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const supabase = createClient();

  return useQuery({
    queryKey: ["current-user", appUser?.id],
    queryFn: async (): Promise<User> => {
      if (!isAuthenticated || !appUser) {
        throw new Error("Authentication required");
      }

      // Return the appUser directly since it's already loaded from app_users
      return appUser as User;
    },
    enabled: isAuthenticated && !!appUser,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllUsers() {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const supabase = createClient();

  return useQuery({
    queryKey: ["all-users"],
    queryFn: async (): Promise<User[]> => {
      if (!isAuthenticated || !appUser) {
        throw new Error("Authentication required");
      }

      // Only allow employees, admins, and CRM users to fetch all users
      const userRole = appUser.roles?.name || appUser.role;
      if (!["wtc_employee", "wtc_admin", "wtc_crm"].includes(userRole || "")) {
        throw new Error("Insufficient permissions");
      }

      // Use centralized query builder
      const query = buildAllUsersQuery(supabase, "first_name", true);
      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch users: ${error.message}`);
      }

      return data || [];
    },
    enabled:
      isAuthenticated &&
      !!appUser &&
      ["wtc_employee", "wtc_admin", "wtc_crm"].includes(appUser.roles?.name || appUser.role || ""),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUserSearch(searchTerm: string) {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const supabase = createClient();

  return useQuery({
    queryKey: ["app_users", "search", searchTerm],
    queryFn: async (): Promise<User[]> => {
      if (!isAuthenticated || !appUser) {
        throw new Error("Authentication required");
      }

      const { data, error } = await supabase
        .from("app_users")
        .select("id, email, first_name, last_name")
        .eq("is_active", true)
        .or(
          `email.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`
        )
        .order("email", { ascending: true })
        .limit(20);

      if (error) {
        throw new Error(`Failed to search users: ${error.message}`);
      }

      return (data || []);
    },
    enabled: isAuthenticated && !!appUser && !!searchTerm && searchTerm.length > 1,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUserById(userId: string) {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const supabase = createClient();

  return useQuery({
    queryKey: ["user", userId],
    queryFn: async (): Promise<User> => {
      if (!isAuthenticated || !appUser) {
        throw new Error("Authentication required");
      }

      // Users can only fetch their own profile unless they're admin/employee/CRM
      const userRole = appUser.roles?.name || appUser.role;
      if (
        userId !== appUser.id &&
        !["wtc_employee", "wtc_admin", "wtc_crm"].includes(userRole || "")
      ) {
        throw new Error("Insufficient permissions");
      }

      const { data, error } = await supabase
        .from("app_users")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch user: ${error.message}`);
      }

      return data;
    },
    enabled: isAuthenticated && !!appUser && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}
